// "Email finance" for the fee slips marked Send to Finance for Payment (VALS).
//
// Emma's template (26 Oct 2026), one email for the fee slips ticked in that group, with
// their invoices attached:
//
//   Subject: CLERK NAME Fee slips to be paid from the Crim budget
//   Good morning, (or Good afternoon - set again when the email is sent)
//
//   Can the following fee slips please be paid from the Crim budget?
//
//   Invoice Number:
//   Client Name:
//   Counsel:
//   Fee:
//   (once per fee slip)
//
//   Thanks
//   Emma
//
// To Geraldine in VALS finance, cc Trevor Plowman. Geraldine's address is the one VALS
// finance emails come from. Trevor's has not been seen on any email the portal holds; it
// follows VALS's first-initial-and-surname pattern and is shown, editable, before sending.

import { DEFAULT_SIGN_OFF, counselOf, greetingFor, invoiceNumberOf, personName } from "./clerkEmail.js";

export const FINANCE_TO = "gnagal@vals.org.au";
export const FINANCE_CC = "tplowman@vals.org.au";

// The status a fee slip waits in, and the one it moves to once the email has gone.
export const SEND_TO_FINANCE = "send_to_finance";
export const SENT_TO_FINANCE = "sent_to_finance";

const money = (value) => (Number.parseFloat(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

// One clerk's name, or each of them when the ticked fee slips come from more than one.
export function clerkNamesOf(rows = []) {
  const names = [...new Set(rows.map((row) => String(row.clerkName || "").trim()).filter(Boolean))];
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

export function financeSubject(rows = []) {
  const clerk = clerkNamesOf(rows);
  return `${clerk ? `${clerk} ` : ""}Fee slips to be paid from the Crim budget`;
}

export function feeSlipLines(row = {}) {
  return [
    `Invoice Number: ${invoiceNumberOf(row) || String(row.invoiceName || "").trim()}`,
    `Client Name: ${personName(row.clientName) || String(row.clientName || "").trim()}`,
    `Counsel: ${counselOf(row)}`,
    `Fee: ${money(row.invoiceAmount)}`,
  ].join("\n");
}

export function financeBody(rows = [], { date = new Date(), signOff = DEFAULT_SIGN_OFF } = {}) {
  return [
    `${greetingFor(date)},`,
    "",
    "Can the following fee slips please be paid from the Crim budget?",
    "",
    rows.map(feeSlipLines).join("\n\n"),
    "",
    "Thanks",
    String(signOff || DEFAULT_SIGN_OFF).trim(),
  ].join("\n");
}

const auDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Once sent, each fee slip moves to Sent to Finance with the date and recipients in its
// notes, so it is not sent twice and the date stays with the line.
export function afterFinanceSent({ notes = "", to = "", cc = "", date = new Date() } = {}) {
  const who = [String(to).trim() && `to ${String(to).trim()}`, String(cc).trim() && `cc ${String(cc).trim()}`]
    .filter(Boolean).join(", ");
  const line = `Sent to finance ${auDate(date)}${who ? ` ${who}` : ""} - Crim budget.`;
  const existing = String(notes || "").trim();
  return { status: SENT_TO_FINANCE, notes: existing ? `${existing}\n${line}` : line };
}

// A file name for an attached invoice: its number and client, never a path.
export function attachmentName(row = {}, index = 0, total = 1) {
  const base = [invoiceNumberOf(row) || "Invoice", personName(row.clientName)].filter(Boolean).join(" - ");
  const suffix = total > 1 ? ` (${index + 1})` : "";
  return `${base}${suffix}`.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 110) + ".pdf";
}
